import { useState, useCallback, useEffect, useRef } from 'react';
import { getUniqueDocumentName } from './useCreateDocumentFromMessage.js';

/**
 * Orchestrates document panel interactions and chat-document lifecycle.
 * Centralizes open/close panel logic, document creation, and per-conversation tracking.
 */
export const useDocumentOrchestrator = ({
  agentHook,
  conversationsHook,
  chatDocumentsHook,
  documentPanelHook,
  createDocumentFromMessage,
}) => {
  const [documentCreatedMessageIdsByConversation, setDocumentCreatedMessageIdsByConversation] =
    useState({});

  const openDocumentPanel = useCallback(
    (options = {}) => {
      const { documentId, messageId, title, content, format = 'text' } = options;

      const createAndOpenDocument = (params = {}) => {
        const docTitle = getUniqueDocumentName(params.title ?? 'Document');
        const docContent = params.content ?? '';
        const docFormat = params.format ?? 'text';
        const conversationId = conversationsHook.conversationId ?? null;
        const id = chatDocumentsHook.addDocument({
          title: docTitle,
          content: docContent,
          contentFormat: docFormat,
          agentUuid: agentHook.selectedAgentId,
          conversationId,
          messageId: params.messageId ?? null,
          module: 'HomePageV2',
        });

        chatDocumentsHook.setCurrentDocument(id);
        documentPanelHook.openDocumentPanel({
          title: docTitle,
          content: docContent,
          messageId: params.messageId ?? null,
          format: docFormat,
        });

        createDocumentFromMessage({ title: docTitle, content: docContent }).then((documentUuid) => {
          if (documentUuid && id) {
            chatDocumentsHook.updateDocument(id, { documentUuid });
          }
        });
      };

      if (documentId) {
        const doc = chatDocumentsHook.getDocument(documentId);
        chatDocumentsHook.setCurrentDocument(documentId);
        documentPanelHook.openDocumentPanel({
          title: doc?.title ?? 'Document',
          content: doc?.content ?? '',
          messageId,
          format: doc?.contentFormat ?? 'text',
        });
      } else if (messageId != null) {
        const conversationId = conversationsHook.conversationId ?? null;
        const existingDoc = chatDocumentsHook.getDocumentByMessageId(messageId, conversationId);
        if (existingDoc) {
          const shouldUpgradeFormat =
            existingDoc.contentFormat === 'text' && (format === 'markdown' || format === 'html');
          const nextContent = shouldUpgradeFormat ? (content ?? existingDoc.content ?? '') : existingDoc.content;
          const nextFormat = shouldUpgradeFormat ? format : existingDoc.contentFormat ?? format;

          if (shouldUpgradeFormat) {
            chatDocumentsHook.updateDocument(existingDoc.id, {
              content: nextContent,
              contentFormat: nextFormat,
            });
          }

          chatDocumentsHook.setCurrentDocument(existingDoc.id);
          documentPanelHook.openDocumentPanel({
            title: existingDoc.title ?? 'Document',
            content: nextContent ?? '',
            messageId,
            format: nextFormat,
          });
        } else {
          createAndOpenDocument({
            title,
            content,
            format,
            messageId,
          });
        }
      } else {
        createAndOpenDocument({
          title,
          content,
          format,
          messageId: null,
        });
      }
    },
    [
      chatDocumentsHook.getDocument,
      chatDocumentsHook.getDocumentByMessageId,
      chatDocumentsHook.addDocument,
      chatDocumentsHook.updateDocument,
      chatDocumentsHook.setCurrentDocument,
      documentPanelHook.openDocumentPanel,
      agentHook.selectedAgentId,
      conversationsHook.conversationId,
      createDocumentFromMessage,
    ]
  );

  const closeDocumentPanel = useCallback(() => {
    documentPanelHook.closeDocumentPanel();
  }, [documentPanelHook]);

  const prevDocumentPanelOpenRef = useRef(documentPanelHook.isDocumentPanelOpen);
  useEffect(() => {
    const wasOpen = prevDocumentPanelOpenRef.current;
    prevDocumentPanelOpenRef.current = documentPanelHook.isDocumentPanelOpen;
    if (wasOpen && !documentPanelHook.isDocumentPanelOpen) {
      const messageId = documentPanelHook.consumeLastOpenedFromMessageId?.();
      const conversationId = conversationsHook.conversationId;
      if (messageId != null && conversationId) {
        setDocumentCreatedMessageIdsByConversation((prev) => {
          const current = prev[conversationId] || [];
          if (current.includes(messageId)) return prev;
          return { ...prev, [conversationId]: [...current, messageId] };
        });
      }
    }
  }, [
    documentPanelHook.isDocumentPanelOpen,
    documentPanelHook.consumeLastOpenedFromMessageId,
    conversationsHook.conversationId,
  ]);

  const removeDocumentCreatedMessageId = useCallback((conversationId, messageId) => {
    if (!conversationId || !messageId) return;
    setDocumentCreatedMessageIdsByConversation((prev) => {
      const current = prev[conversationId] || [];
      if (!current.includes(messageId)) return prev;
      return { ...prev, [conversationId]: current.filter((id) => id !== messageId) };
    });
  }, []);

  return {
    openDocumentPanel,
    closeDocumentPanel,
    documentCreatedMessageIdsByConversation,
    removeDocumentCreatedMessageId,
  };
};
